import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Amenities from '../components/Amenities';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
              About Laksh Space
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-[#e30613] to-[#c1050f] mx-auto rounded-full mb-8"></div>
            <p className="text-2xl text-[#e30613] font-bold mb-8">
              Work. Connect. Inspire.
            </p>
          </div>

          {/* Main Description */}
          <div className="max-w-5xl mx-auto space-y-6 mb-16">
            <p className="text-xl text-gray-700 leading-relaxed">
              Laksh Space is redefining the coworking and private office experience in Bengaluru by creating 
              thoughtfully designed, flexible, and professional workspaces that inspire productivity and 
              collaboration.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              With a strong presence in R.T. Nagar, Laksh Space caters to startups, freelancers, 
              entrepreneurs, and growing businesses seeking a modern workspace that blends comfort, 
              functionality, and community. Our goal is to provide high-quality office solutions — including 
              private cabins, coworking desks, meeting rooms, training rooms, and virtual offices — that 
              empower professionals to work efficiently and grow meaningfully.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              We go beyond providing just space — Laksh Space offers a professional ecosystem that supports 
              networking, business growth, and personal development. Every detail is designed with people in 
              mind, ensuring an environment that fosters focus, collaboration, and inspiration.
            </p>
          </div>
        </div>
      </div>

      {/* Vision & Mission Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vision */}
            <div className="bg-gradient-to-br from-[#e30613] to-[#c1050f] rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6">Our Vision</h2>
              <p className="text-lg leading-relaxed mb-4">
                To empower professionals and businesses by providing inspiring, accessible, and future-ready 
                workspaces that elevate productivity and growth.
              </p>
              <p className="text-white/90 italic">
                Everything we do is driven by our belief in enabling people to Work. Connect. Inspire.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-lg leading-relaxed mb-4">
                To become one of India's most trusted and admired workspace providers by offering high-quality, 
                flexible, and community-driven office solutions that cater to evolving professional needs.
              </p>
              <p className="text-white/90 italic">
                We aim to build an ecosystem where businesses thrive, ideas grow, and work becomes an 
                inspiring experience.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Core Values Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Value 1 */}
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#e30613] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Client-Centric Approach</h3>
              <p className="text-gray-600 leading-relaxed">
                We prioritize the needs of our clients — entrepreneurs, startups, and corporates alike — by 
                offering tailored workspace solutions and exceptional support that enhance their business 
                journey.
              </p>
            </div>

            {/* Value 2 */}
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#e30613] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Collaboration & Community</h3>
              <p className="text-gray-600 leading-relaxed">
                We believe that great work happens in great company. Our spaces are designed to foster 
                collaboration, knowledge sharing, and a strong sense of community among professionals.
              </p>
            </div>

            {/* Value 3 */}
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#e30613] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quality & Professionalism</h3>
              <p className="text-gray-600 leading-relaxed">
                Every Laksh Space location upholds high standards of design, maintenance, and service, 
                ensuring a professional environment that reflects the values and aspirations of our members.
              </p>
            </div>

            {/* Value 4 */}
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#e30613] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Employee Engagement & Growth</h3>
              <p className="text-gray-600 leading-relaxed">
                We believe happy teams create better experiences. By maintaining a positive work culture, we 
                ensure our team's motivation translates into meaningful service for our clients.
              </p>
            </div>

            {/* Value 5 */}
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#e30613] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">5</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Value & Transparency</h3>
              <p className="text-gray-600 leading-relaxed">
                We strive to offer transparent, cost-effective solutions without compromising on comfort, 
                quality, or functionality — ensuring that every client receives true value for their investment.
              </p>
            </div>

            {/* Value 6 */}
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#e30613] rounded-lg flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">6</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Long-Term Commitment</h3>
              <p className="text-gray-600 leading-relaxed">
                We focus on sustainable, long-term relationships built on trust and reliability, helping businesses 
                grow with stability and confidence.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Closing Section */}
      <div className="bg-gradient-to-br from-[#e30613] to-[#c1050f] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Laksh Space – Work. Connect. Inspire.</h2>
          <p className="text-xl leading-relaxed">
            A place where productivity meets passion, and your workspace becomes a source of growth, 
            creativity, and success.
          </p>
        </div>
      </div>

      <Amenities title="Our Premium Amenities" />

      <Footer />
    </div>
  );
};

export default AboutPage;